import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { all_routes } from "../router/all_routes";
import { API_URL } from "../../environment";
import { useManutencao, MSG_MANUTENCAO } from "../auth/login/maintenance";
import usePwaMobile from "./usePwaMobile";
import "./LoginMobile.css";

/* Limpa cache/estado de um login anterior antes de gravar o novo (igual login-3). */
const limparCacheCompleto = async () => {
  try {
    localStorage.clear();
    sessionStorage.clear();
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
    if (window.indexedDB && indexedDB.databases) {
      const dbs = await indexedDB.databases();
      dbs.forEach((db) => {
        if (db.name) indexedDB.deleteDatabase(db.name);
      });
    }
  } catch (e) {
    console.error("Erro ao limpar cache:", e);
  }
};

/* Login enxuto para o app mobile (/mobile-app/login).
   Replica fielmente a autenticação do login-3 desktop: mesmo endpoint /login,
   mesmo payload {user,pwd} e as mesmas chaves no localStorage — assim o
   ChatMobile carrega as conversas igual ao desktop. Ao logar, vai pro /mobile-app. */
const LoginMobile: React.FC = () => {
  const navigate = useNavigate();
  usePwaMobile(); // manifest + ícone ConversAI + service worker (PWA instalável)
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  // Aviso de manutencao: quem decide e o banco (crm_manutencao), via GET /api/manutencao.
  const mostrarManutencao = useManutencao();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    setError("");

    try {
      const response = await fetch(`${API_URL}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user: email, pwd: password }),
      });
      const data = await response.json();

      if (response.ok) {
        await limparCacheCompleto();

        localStorage.setItem("id_cliente", data.id_cliente);
        localStorage.setItem("id_grupo", data.id_grupo);
        localStorage.setItem("id_user", data.id_user);
        localStorage.setItem("user_email", email);
        localStorage.setItem("isConnected", "false");
        localStorage.setItem("fone_ins", data.fone);
        if (data.nome_time) localStorage.setItem("nome_time", data.nome_time);
        else localStorage.removeItem("nome_time");

        // Supabase Realtime (chat instantâneo): token por tenant + url/anon
        if (data.supabase_token) localStorage.setItem("supabase_token", data.supabase_token);
        else localStorage.removeItem("supabase_token");
        if (data.supabase_url) localStorage.setItem("supabase_url", data.supabase_url);
        if (data.supabase_anon_key) localStorage.setItem("supabase_anon_key", data.supabase_anon_key);
        // Realtime do projeto CRM (leads/funil_fluxo)
        if (data.supabase_crm_token) localStorage.setItem("supabase_crm_token", data.supabase_crm_token);
        else localStorage.removeItem("supabase_crm_token");
        if (data.supabase_crm_url) localStorage.setItem("supabase_crm_url", data.supabase_crm_url);
        if (data.supabase_crm_anon_key) localStorage.setItem("supabase_crm_anon_key", data.supabase_crm_anon_key);

        if (data.instancia_evo) localStorage.setItem("instanceName", data.instancia_evo);

        if (data.admin) localStorage.setItem("isAdmin", "true");
        else localStorage.removeItem("isAdmin");

        if (data.admin_master) localStorage.setItem("isAdminMaster", "true");
        else localStorage.removeItem("isAdminMaster");

        if (data.instancia && data.instancia !== 0 && data.instancia !== "0") {
          localStorage.setItem("instanciaoficial", data.instancia);
        } else {
          localStorage.removeItem("instanciaoficial");
        }

        if (data.nome_pai) localStorage.setItem("nome_pai", data.nome_pai);

        navigate(all_routes.mobileApp, { replace: true });
      } else {
        setError(data.error || "E-mail ou senha incorretos.");
        setLoading(false);
      }
    } catch (err) {
      setError("Não foi possível conectar ao servidor.");
      setLoading(false);
    }
  };

  return (
    <div className="lmob">
      <form className="lmob-card" onSubmit={handleLogin}>
        {/* Logo oficial (ícone + "ConversAI CRM"): a marca já vem escrita na
            imagem, então não há título nem subtítulo em texto ao lado. */}
        <div className="lmob-brand">
          <img className="lmob-logo" src="/mobile-logo-crm.png" alt="ConversAI CRM" />
        </div>

        {mostrarManutencao && (
          <div
            role="alert"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              background: "#fef3c7",
              border: "1px solid #f59e0b",
              color: "#92400e",
              borderRadius: 10,
              padding: "10px 12px",
              marginBottom: 16,
              fontSize: 13,
              fontWeight: 600,
              lineHeight: 1.4,
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            <span>{MSG_MANUTENCAO}</span>
          </div>
        )}

        {/* Sem rótulo visível (o placeholder faz o papel, como no layout aprovado):
            o nome do campo vai no aria-label pra leitor de tela não ficar mudo. */}
        <label className="lmob-field">
          <input
            type="text"
            inputMode="email"
            aria-label="Usuário"
            autoCapitalize="none"
            autoCorrect="off"
            autoComplete="username"
            value={email}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
            placeholder="usuário"
            required
          />
        </label>

        <label className="lmob-field">
          <div className="lmob-pwd">
            <input
              type={showPwd ? "text" : "password"}
              aria-label="Senha"
              autoComplete="current-password"
              value={password}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
              placeholder="senha"
              required
            />
            <button
              type="button"
              className="lmob-eye"
              aria-label={showPwd ? "Ocultar senha" : "Mostrar senha"}
              onClick={() => setShowPwd((v) => !v)}
            >
              {showPwd ? "ocultar" : "ver"}
            </button>
          </div>
        </label>

        {error && <div className="lmob-err">{error}</div>}

        <button type="submit" className="lmob-btn" disabled={loading}>
          {loading ? "ENTRANDO..." : "ENTRAR"}
        </button>
      </form>
    </div>
  );
};

export default LoginMobile;
